import React from "react";

const GBET_URL = "https://gbetpools.com";

/**
 * Renders a pool result in the ableFAST-style vertical format.
 *
 * WEEK X
 * Football Pools Results
 * Stake your Aba High odds on Gbetpools.com   (link)
 * | 07 |
 * | 09 |
 * ...
 * TOTAL = X DRAWS
 * Stake your Aba High odds on Gbetpools.com   (link)
 * FINAL RESULT.
 *
 * Draw numbers are extracted from post.matches (each with coupon_number & result==="draw").
 * If matches are missing, falls back to parsing the HTML content for numbers between "|".
 */
const extractDrawNumbers = (post) => {
  const matches = Array.isArray(post?.matches) ? post.matches : [];
  let nums = matches
    .filter((m) => (m?.result || "").toLowerCase() === "draw")
    .map((m) => Number(m?.coupon_number))
    .filter((n) => Number.isFinite(n) && n > 0);

  if (nums.length === 0 && typeof post?.content === "string") {
    // Fallback: try to parse "Draw Numbers: 07, 09, 11..." or table rows
    const html = post.content;
    // First try explicit "Draw Numbers: X, Y, Z"
    const m1 = html.match(/Draw\s*Numbers?\s*[:\-]?\s*<\/?[^>]*>?\s*([0-9,\s]+)/i);
    if (m1) {
      nums = m1[1].split(/[,\s]+/).map((s) => parseInt(s, 10)).filter((n) => Number.isFinite(n) && n > 0);
    }
    if (nums.length === 0) {
      // Otherwise pick numbers inside first-column <td><strong>NN</strong></td>
      const tdRe = /<td[^>]*>\s*<strong>\s*(\d{1,2})\s*<\/strong>\s*<\/td>/gi;
      let mm;
      while ((mm = tdRe.exec(html)) !== null) {
        nums.push(parseInt(mm[1], 10));
      }
    }
  }
  // Dedupe & sort ascending
  return Array.from(new Set(nums)).sort((a, b) => a - b);
};

const StakeLink = () => (
  <a
    href={GBET_URL}
    target="_blank"
    rel="noopener noreferrer"
    className="text-blue-700 hover:text-blue-900 underline underline-offset-2 font-semibold"
    data-testid="gbet-stake-link"
  >
    Stake your Aba High odds on Gbetpools.com
  </a>
);

const PoolResultDisplay = ({ post }) => {
  const draws = extractDrawNumbers(post);
  const total = draws.length;
  const week = post?.week_number || "-";
  const hasResults = total > 0;

  return (
    <div className="font-serif text-gray-800 text-center py-6 px-4" data-testid={`pool-result-display-week-${week}`}>
      <h2 className="text-2xl sm:text-3xl font-bold tracking-wide">WEEK {week}</h2>
      <p className="text-lg sm:text-xl font-semibold mt-1">Football Pools Results</p>
      <div className="mt-2 text-base sm:text-lg">
        <StakeLink />
      </div>

      {hasResults ? (
        <div className="mt-5 space-y-1.5 text-lg sm:text-xl font-medium">
          {draws.map((n) => (
            <div key={n} data-testid={`draw-number-${n}`}>
              {`| ${String(n).padStart(2, "0")} |`}
            </div>
          ))}
        </div>
      ) : (
        <p className="mt-5 text-gray-500 italic">Results pending — check back after kickoff.</p>
      )}

      {hasResults && (
        <p className="mt-5 text-base sm:text-lg">TOTAL = {total} DRAWS</p>
      )}

      <div className="mt-3 text-base sm:text-lg">
        <StakeLink />
      </div>

      <p className="mt-3 text-sm sm:text-base tracking-wide text-gray-700">
        {hasResults ? "FINAL RESULT." : "AWAITING FINAL RESULT."}
      </p>
    </div>
  );
};

export default PoolResultDisplay;
